import { __awaiter, __generator, __assign } from '../node_modules/tslib/tslib.es6.js';
import { Logger } from '../utils/logger.js';

// CrossDomainStorage.ts - Storage API compliant cross-domain storage
/**
 * Cross-domain storage that conforms to the Storage API
 * Requires one async sync() call to pull all data from the bridge domain
 * Supports stage isolation (beta, gamma, prod, etc.)
 * Falls back to localStorage with stage prefixing if bridge fails
 */
var CrossDomainStorage = /** @class */ (function () {
    function CrossDomainStorage(bridgeOrigin, stage, bridgePath) {
        if (bridgePath === void 0) { bridgePath = '/bridge.html'; }
        this.bridge = null;
        this.cache = new Map();
        this.isReady = false;
        this.isSyncing = null;
        this.maxKeyLength = 1024;
        this.maxValueLength = 1024 * 1024; // 1MB per value
        this.maxStorageSize = 10 * 1024 * 1024; // 10MB total
        this.usingFallback = false;
        // Initialize logger first
        this.logger = new Logger('CrossDomainStorage', stage);
        // Validate stage
        if (typeof stage !== 'string' || !stage.trim()) {
            throw new Error('Invalid stage - must be a non-empty string');
        }
        this.bridgeOrigin = bridgeOrigin;
        this.bridgePath = bridgePath;
        this.stage = stage.trim();
        this.fallbackPrefix = "cds_".concat(this.stage, "_");
        // Generate unique sender ID for this instance
        this.senderId = 'storage_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
        // bind the methods to the instance
        this.getItem = this.getItem.bind(this);
        this.setItem = this.setItem.bind(this);
        this.removeItem = this.removeItem.bind(this);
        this.clear = this.clear.bind(this);
    }
    CrossDomainStorage.createInstance = function (bridgeOrigin, stage, bridgePath) {
        if (bridgePath === void 0) { bridgePath = '/bridge.html'; }
        var key = "".concat(bridgeOrigin, "_").concat(stage, "_").concat(bridgePath);
        if (!CrossDomainStorage.instanceByOrigin.has(key)) {
            var instance_1 = new CrossDomainStorage(bridgeOrigin, stage, bridgePath);
            CrossDomainStorage.instanceByOrigin.set(key, instance_1);
        }
        var instance = CrossDomainStorage.instanceByOrigin.get(key);
        if (!instance) {
            throw new Error("CrossDomainStorage instance not found for key: ".concat(key));
        }
        CrossDomainStorage.currentInstance = instance;
        return CrossDomainStorage.currentInstance;
    };
    CrossDomainStorage.getCurrentInstance = function () {
        if (!CrossDomainStorage.currentInstance) {
            return null;
        }
        return CrossDomainStorage.currentInstance;
    };
    /**
     * Async sync function - call this once to pull all data from bridge domain
     */
    CrossDomainStorage.prototype.initialize = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (this.isSyncing) {
                            return [2 /*return*/, this.isSyncing];
                        }
                        this.isSyncing = this.performSync();
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, , 3, 4]);
                        return [4 /*yield*/, this.isSyncing];
                    case 2:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        this.isSyncing = null;
                        return [7 /*endfinally*/];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    CrossDomainStorage.prototype.performSync = function () {
        return __awaiter(this, void 0, void 0, function () {
            var allData, totalSize, _i, _a, _b, key, value, error_1;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _c.trys.push([0, 4, , 6]);
                        if (!!this.bridge) return [3 /*break*/, 2];
                        this.logger.debug("Initializing bridge for stage '".concat(this.stage, "' with origin '").concat(this.bridgeOrigin, "'"));
                        return [4 /*yield*/, this.initializeBridge()];
                    case 1:
                        _c.sent();
                        _c.label = 2;
                    case 2: return [4 /*yield*/, this.sendMessage({
                            action: 'getAll',
                            messageId: this.generateMessageId(),
                            stage: this.stage
                        })];
                    case 3:
                        allData = _c.sent();
                        this.logger.debug("Received all data from bridge:", allData);
                        this.cache.clear();
                        if (allData && typeof allData === 'object') {
                            totalSize = 0;
                            for (_i = 0, _a = Object.entries(allData); _i < _a.length; _i++) {
                                _b = _a[_i], key = _b[0], value = _b[1];
                                if (typeof key === 'string' && typeof value === 'string') {
                                    totalSize += key.length + value.length;
                                    if (totalSize > this.maxStorageSize) {
                                        this.logger.warn('Storage size limit exceeded, truncating data');
                                        break;
                                    }
                                    this.cache.set(key, value);
                                }
                            }
                        }
                        this.isReady = true;
                        this.logger.debug("Stage '".concat(this.stage, "' sync completed via bridge. Loaded ").concat(this.cache.size, " items:"), this.cache);
                        return [3 /*break*/, 6];
                    case 4:
                        error_1 = _c.sent();
                        this.logger.warn("Bridge sync failed for stage '".concat(this.stage, "', falling back to localStorage:"), error_1);
                        return [4 /*yield*/, this.initializeFallback()];
                    case 5:
                        _c.sent();
                        return [3 /*break*/, 6];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Initialize fallback mode using localStorage
     */
    CrossDomainStorage.prototype.initializeFallback = function () {
        return __awaiter(this, void 0, void 0, function () {
            var i, key, storageKey, value;
            return __generator(this, function (_a) {
                try {
                    this.usingFallback = true;
                    this.cache.clear();
                    // Load all items from localStorage that match our prefix
                    for (i = 0; i < localStorage.length; i++) {
                        key = localStorage.key(i);
                        if (key === null || key === void 0 ? void 0 : key.startsWith(this.fallbackPrefix)) {
                            storageKey = key.substring(this.fallbackPrefix.length);
                            value = localStorage.getItem(key);
                            if (value !== null) {
                                this.cache.set(storageKey, value);
                            }
                        }
                    }
                    this.isReady = true;
                    this.logger.info("Fallback mode initialized for stage '".concat(this.stage, "'. Loaded ").concat(this.cache.size, " items from localStorage"));
                }
                catch (error) {
                    this.logger.error("Failed to initialize localStorage fallback:", error);
                    throw new Error('Both bridge and localStorage fallback failed');
                }
                return [2 /*return*/];
            });
        });
    };
    // Storage API Implementation - All synchronous
    CrossDomainStorage.prototype.getItem = function (key) {
        var _a;
        this.ensureReady();
        this.validateKey(key);
        var value = (_a = this.cache.get(key)) !== null && _a !== void 0 ? _a : null;
        this.logger.debug("getItem('".concat(key, "') -> ").concat(value ? "'".concat(value.substring(0, 50)).concat(value.length > 50 ? '...' : '', "'") : 'null'));
        return value;
    };
    CrossDomainStorage.prototype.setItem = function (key, value) {
        this.ensureReady();
        this.validateKey(key);
        this.validateValue(value);
        // Check total storage size
        var currentSize = this.getStorageInfo().totalSize;
        var newItemSize = key.length + value.length;
        var oldValue = this.cache.get(key);
        var oldItemSize = oldValue ? key.length + oldValue.length : 0;
        if (currentSize - oldItemSize + newItemSize > this.maxStorageSize) {
            this.logger.error("Storage quota exceeded. Current: ".concat(currentSize, ", New item: ").concat(newItemSize, ", Max: ").concat(this.maxStorageSize));
            throw new Error('Storage quota exceeded');
        }
        this.cache.set(key, value);
        this.logger.debug("setItem('".concat(key, "', '").concat(value.substring(0, 50)).concat(value.length > 50 ? '...' : '', "') - Size: ").concat(newItemSize, " bytes ").concat(this.usingFallback ? '(fallback mode)' : ''));
        // Update persistent storage (handles both bridge and fallback modes)
        this.updateBridge('setItem', key, value);
    };
    CrossDomainStorage.prototype.removeItem = function (key) {
        this.ensureReady();
        this.validateKey(key);
        var hadKey = this.cache.has(key);
        this.cache.delete(key);
        this.logger.debug("removeItem('".concat(key, "') - Key ").concat(hadKey ? 'existed' : 'did not exist', " ").concat(this.usingFallback ? '(fallback mode)' : ''));
        // Update persistent storage (handles both bridge and fallback modes)
        this.updateBridge('removeItem', key);
    };
    CrossDomainStorage.prototype.clear = function () {
        this.ensureReady();
        var itemCount = this.cache.size;
        this.cache.clear();
        this.logger.debug("clear() - Removed ".concat(itemCount, " items ").concat(this.usingFallback ? '(fallback mode)' : ''));
        // Update persistent storage (handles both bridge and fallback modes)
        this.updateBridge('clear');
    };
    CrossDomainStorage.prototype.key = function (index) {
        var _a;
        this.ensureReady();
        if (index < 0 || index >= this.cache.size) {
            return null;
        }
        return (_a = Array.from(this.cache.keys())[index]) !== null && _a !== void 0 ? _a : null;
    };
    Object.defineProperty(CrossDomainStorage.prototype, "length", {
        get: function () {
            this.ensureReady();
            return this.cache.size;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(CrossDomainStorage.prototype, "currentStage", {
        // Additional utility methods
        /**
         * Get the current stage
         */
        get: function () {
            return this.stage;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Get all keys as array
     */
    CrossDomainStorage.prototype.keys = function () {
        this.ensureReady();
        return Array.from(this.cache.keys());
    };
    Object.defineProperty(CrossDomainStorage.prototype, "ready", {
        /**
         * Check if storage is ready (synced)
         */
        get: function () {
            return this.isReady;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(CrossDomainStorage.prototype, "usingLocalStorageFallback", {
        /**
         * Check if using localStorage fallback mode
         */
        get: function () {
            return this.usingFallback;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(CrossDomainStorage.prototype, "storageMode", {
        /**
         * Get the storage mode description
         */
        get: function () {
            return this.usingFallback ? 'localStorage fallback' : 'cross-domain bridge';
        },
        enumerable: false,
        configurable: true
    });
    // Private methods
    CrossDomainStorage.prototype.ensureReady = function () {
        if (!this.isReady) {
            throw new Error('CrossDomainStorage not ready. Call sync() first.');
        }
    };
    CrossDomainStorage.prototype.initializeBridge = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                return [2 /*return*/, new Promise(function (resolve, reject) {
                        var _a;
                        // Create unique bridge ID based on origin and stage using hash
                        var bridgeId = _this.hashString("cross-domain-storage-".concat(_this.stage));
                        // Check if bridge already exists
                        var existingBridge = document.getElementById(bridgeId);
                        if (existingBridge) {
                            _this.logger.debug("Reusing existing bridge iframe with ID: ".concat(bridgeId));
                            _this.bridge = existingBridge;
                            resolve();
                            return;
                        }
                        // Remove any old bridge for this instance
                        if ((_a = _this.bridge) === null || _a === void 0 ? void 0 : _a.parentNode) {
                            _this.logger.debug('Removing old bridge iframe before creating new one');
                            _this.bridge.parentNode.removeChild(_this.bridge);
                        }
                        var url = "".concat(_this.bridgeOrigin).concat(_this.bridgePath);
                        _this.bridge = document.createElement('iframe');
                        _this.bridge.id = bridgeId;
                        _this.bridge.src = url;
                        _this.bridge.style.display = 'none';
                        _this.bridge.style.width = '0';
                        _this.bridge.style.height = '0';
                        _this.bridge.setAttribute('data-stage', _this.stage);
                        _this.bridge.setAttribute('data-added-by', _this.senderId);
                        _this.bridge.setAttribute('data-timestamp', new Date().toISOString());
                        _this.bridge.onload = function () {
                            var _a;
                            try {
                                var contentWindow = (_a = _this.bridge) === null || _a === void 0 ? void 0 : _a.contentWindow;
                                if (contentWindow) {
                                    _this.logger.debug("Storage bridge iframe loaded successfully with ID: ".concat(bridgeId, " and origin: ").concat(_this.bridgeOrigin, " and stage: ").concat(_this.stage));
                                    resolve();
                                }
                                else {
                                    _this.logger.error("Failed to load storage bridge iframe with ID: ".concat(bridgeId, " and origin: ").concat(_this.bridgeOrigin, " and stage: ").concat(_this.stage));
                                    reject(new Error('Failed to load storage bridge'));
                                }
                            }
                            catch (error) {
                                _this.logger.error("Failed to load storage bridge iframe with ID: ".concat(bridgeId, " and origin: ").concat(_this.bridgeOrigin, " and stage: ").concat(_this.stage), error);
                                reject(new Error('Failed to load storage bridge iframe'));
                            }
                        };
                        _this.bridge.onerror = function () {
                            _this.logger.error('Failed to load storage bridge iframe');
                            reject(new Error('Failed to load storage bridge'));
                        };
                        document.body.appendChild(_this.bridge);
                        _this.logger.debug("Created new bridge iframe with ID: ".concat(bridgeId));
                    })];
            });
        });
    };
    /**
     * Simple hash function to create short, consistent identifiers
     */
    CrossDomainStorage.prototype.hashString = function (str) {
        var hash = 0;
        if (str.length === 0)
            return hash.toString(36);
        for (var i = 0; i < str.length; i++) {
            var char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        return Math.abs(hash).toString(36);
    };
    CrossDomainStorage.prototype.sendMessage = function (message) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_b) {
                if (!((_a = this.bridge) === null || _a === void 0 ? void 0 : _a.contentWindow)) {
                    this.logger.error('Bridge not initialized when trying to send message');
                    throw new Error('Bridge not initialized');
                }
                this.logger.debug("Sending message: ".concat(message.action, " (").concat(message.messageId, ")"));
                return [2 /*return*/, new Promise(function (resolve, reject) {
                        var _a, _b;
                        var messageId = message.messageId;
                        var resolved = false;
                        var timeout = setTimeout(function () {
                            if (!resolved) {
                                resolved = true;
                                window.removeEventListener('message', handler);
                                _this.logger.error("Storage bridge timeout for action: ".concat(message.action, " (").concat(messageId, ")"));
                                reject(new Error("Storage bridge timeout for action: ".concat(message.action)));
                            }
                        }, 2000);
                        var handler = function (event) {
                            // Security: Validate origin
                            if (event.origin !== _this.bridgeOrigin) {
                                _this.logger.warn("Ignoring message from invalid origin: ".concat(event.origin, " (expected: ").concat(_this.bridgeOrigin, ")"));
                                return;
                            }
                            // Validate message structure
                            if (!event.data || typeof event.data !== 'object') {
                                _this.logger.warn('Ignoring message with invalid structure:', event.data);
                                return;
                            }
                            if (event.data.messageId !== messageId) {
                                _this.logger.warn("Ignoring message with wrong ID: ".concat(event.data.messageId, " (expected: ").concat(messageId, ")"));
                                return;
                            }
                            // Ignore messages from self using sender ID
                            if (event.data.senderId === _this.senderId) {
                                _this.logger.debug("Ignoring message from self: ".concat(event.data.senderId));
                                return;
                            }
                            if (!resolved) {
                                resolved = true;
                                clearTimeout(timeout);
                                window.removeEventListener('message', handler);
                                // Check for error in response
                                if (event.data.success === false) {
                                    _this.logger.error("Storage operation failed: ".concat(event.data.error || 'Unknown error'));
                                    reject(new Error(event.data.error || 'Storage operation failed'));
                                }
                                else {
                                    _this.logger.debug("Received successful response for ".concat(message.action, " (").concat(messageId, ")"));
                                    resolve(event.data.data);
                                }
                            }
                        };
                        window.addEventListener('message', handler);
                        try {
                            // Add our sender ID to the message
                            var messageWithSender = __assign(__assign({}, message), { senderId: _this.senderId });
                            (_b = (_a = _this.bridge) === null || _a === void 0 ? void 0 : _a.contentWindow) === null || _b === void 0 ? void 0 : _b.postMessage(messageWithSender, _this.bridgeOrigin);
                            _this.logger.debug("Message posted to bridge: ".concat(message.action, " (").concat(messageId, ")"));
                        }
                        catch (error) {
                            if (!resolved) {
                                resolved = true;
                                clearTimeout(timeout);
                                window.removeEventListener('message', handler);
                                var errorMessage = error instanceof Error ? error.message : 'Unknown error';
                                _this.logger.error("Failed to send message to bridge: ".concat(errorMessage));
                                reject(new Error("Failed to send message: ".concat(errorMessage)));
                            }
                        }
                    })];
            });
        });
    };
    CrossDomainStorage.prototype.updateBridge = function (action, key, value) {
        var _a;
        // Handle fallback mode
        if (this.usingFallback) {
            try {
                switch (action) {
                    case 'setItem':
                        if (key && value !== undefined) {
                            localStorage.setItem(this.fallbackPrefix + key, value);
                        }
                        break;
                    case 'removeItem':
                        if (key) {
                            localStorage.removeItem(this.fallbackPrefix + key);
                        }
                        break;
                    case 'clear': {
                        var keysToRemove = [];
                        for (var i = 0; i < localStorage.length; i++) {
                            var storageKey = localStorage.key(i);
                            if (storageKey === null || storageKey === void 0 ? void 0 : storageKey.startsWith(this.fallbackPrefix)) {
                                keysToRemove.push(storageKey);
                            }
                        }
                        keysToRemove.forEach(function (k) { return localStorage.removeItem(k); });
                        break;
                    }
                }
                this.logger.debug("Background fallback update: ".concat(action, " ").concat(key ? "(".concat(key, ")") : ''));
            }
            catch (error) {
                this.logger.warn("Failed to update localStorage in fallback mode:", error);
            }
            return;
        }
        // Handle bridge mode
        if (!((_a = this.bridge) === null || _a === void 0 ? void 0 : _a.contentWindow)) {
            this.logger.warn('Bridge not available for background update');
            return;
        }
        try {
            var messageId = this.generateMessageId();
            // Fire and forget - don't wait for response
            this.bridge.contentWindow.postMessage({
                action: action,
                key: key,
                value: value,
                messageId: messageId,
                senderId: this.senderId,
                stage: this.stage
            }, this.bridgeOrigin);
            this.logger.debug("Background bridge update: ".concat(action, " (").concat(messageId, ")"));
        }
        catch (error) {
            this.logger.warn('Failed to update bridge storage:', error);
        }
    };
    CrossDomainStorage.prototype.generateMessageId = function () {
        return Math.random().toString(36).substring(2) + Date.now().toString(36);
    };
    // Cleanup
    CrossDomainStorage.prototype.destroy = function () {
        var _a;
        this.logger.info("Destroying CrossDomainStorage instance (mode: ".concat(this.storageMode, ")"));
        // Only remove bridge if we're the only one using it
        if ((_a = this.bridge) === null || _a === void 0 ? void 0 : _a.parentNode) {
            var bridgeId = this.bridge.id;
            var stage = this.bridge.getAttribute('data-stage');
            // Check if other instances might be using this bridge
            // For now, we'll just detach our reference but leave the bridge in DOM
            // A more sophisticated approach would be to use a reference counter
            this.logger.debug("Detaching from bridge iframe with ID: ".concat(bridgeId, " (stage: ").concat(stage, ")"));
            this.bridge = null;
            // Note: We're not removing the bridge from DOM as other instances might be using it
            // In a production environment, you might want to implement a reference counting system
        }
        var itemCount = this.cache.size;
        this.cache.clear();
        this.isReady = false;
        this.usingFallback = false;
        this.logger.debug("Cleared cache (".concat(itemCount, " items) and reset ready state"));
    };
    /**
     * Force remove the bridge iframe (use with caution)
     * This will affect all storage instances using the same bridge
     */
    CrossDomainStorage.prototype.forceDestroyBridge = function () {
        var _a;
        if ((_a = this.bridge) === null || _a === void 0 ? void 0 : _a.parentNode) {
            var bridgeId = this.bridge.id;
            this.logger.warn("Force removing bridge iframe with ID: ".concat(bridgeId));
            this.bridge.parentNode.removeChild(this.bridge);
            this.bridge = null;
        }
    };
    /**
     * Set the log level for this storage instance
     * @param level - The log level to set (DEBUG, INFO, WARN, ERROR, NONE)
     */
    CrossDomainStorage.prototype.setLogLevel = function (level) {
        this.logger.setLogLevel(level);
        this.logger.info("Log level changed to ".concat(level));
    };
    /**
     * Get current storage usage information
     */
    CrossDomainStorage.prototype.getStorageInfo = function () {
        var _this = this;
        var totalSize = this.keys().reduce(function (acc, key) { var _a, _b; return acc + key.length + ((_b = (_a = _this.getItem(key)) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0); }, 0);
        var utilizationPercent = (totalSize / this.maxStorageSize) * 100;
        var info = {
            itemCount: this.cache.size,
            totalSize: totalSize,
            maxSize: this.maxStorageSize,
            utilizationPercent: Math.round(utilizationPercent * 100) / 100,
            ready: this.isReady,
            stage: this.stage,
            usingFallback: this.usingFallback,
            storageMode: this.storageMode
        };
        this.logger.debug('Storage info requested:', info);
        return info;
    };
    // Private validation methods
    CrossDomainStorage.prototype.validateKey = function (key) {
        if (typeof key !== 'string') {
            throw new TypeError('Storage key must be a string');
        }
        if (key.length === 0) {
            throw new Error('Storage key cannot be empty');
        }
        if (key.length > this.maxKeyLength) {
            throw new Error("Storage key too long (max ".concat(this.maxKeyLength, " characters)"));
        }
        // Prevent prototype pollution
        if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
            throw new Error('Invalid storage key');
        }
    };
    CrossDomainStorage.prototype.validateValue = function (value) {
        if (typeof value !== 'string') {
            throw new TypeError('Storage value must be a string');
        }
        if (value.length > this.maxValueLength) {
            throw new Error("Storage value too long (max ".concat(this.maxValueLength, " characters)"));
        }
    };
    CrossDomainStorage.instanceByOrigin = new Map();
    CrossDomainStorage.currentInstance = null;
    return CrossDomainStorage;
}());

export { CrossDomainStorage as default };
//# sourceMappingURL=CrossDomainStorage.js.map
